'use client';

import {
    addItemToCart,
    addShippingMethod,
    applyPointDiscount as applyPointDiscountAction,
    calculateUserCart,
    checkout,
    checkReservedPoints,
    createKbankQROrder,
    createXenditInstallmentPayment,
    createXenditPaymentLink,
    deleteAllCartItems,
    deleteCartItem,
    getCurrency,
    getPointspotSettings,
    getUserCart,
    inquireKbankQR,
    payment2c2p,
    paymentCod,
    paymentKbank,
    paymentNone,
    paymentOmise,
    setCartConditionValue,
    setOrderCoupon,
    setPointspotPhoneNumber,
    updateCartItemQuantity,
    updateIdCard,
    updateOrderAddress,
    updateTaxInvoiceAddress,
} from '@/actions/cart';
import { getShippingMethodCost } from '@/actions/shipping';
import { useTranslation } from '@/app/i18n/client';
import { list_payment_channels } from '@/lib/xendit';
import { convertToSmallestUnit, Currency } from '@/util/formatCurrencyFormat';
import {
    AddItemToCartRequestParams,
    DeleteItemRequestParams,
    UpdateItemQuantityRequestParams,
} from '@/util/services/cart/api';
import {
    SaleOrderItem,
    ShippingMethodCostResult,
} from '@/util/services/cart/api.type';
import {
    buildGtmCartItem,
    pushAddToCart,
    pushRemoveFromCart,
} from '@/components/gtm/gtm-cart-events';
import { ShippingMethod } from '@prisma/client';
import { LOCALIZATION } from '@prisma/main';
import React, { createContext, useContext, useState } from 'react';
import {
    APIPaymentResult,
    AXIDCardResult,
    Cart,
} from '../../util/services/cart/api.type';

export type PaymentMethod =
    | 'CREDIT_CARD'
    | 'TRANSFER'
    | 'QR_PAYMENT'
    | 'NONE'
    | 'COD'
    | 'E_WALLET'
    | 'MOBILE_BANKING'
    | 'DIRECT_DEBIT'
    | 'INSTALLMENT';

interface CartContextData {
    cart?: Cart;
    isUpdating: boolean;
    isImporting: boolean;
    handleSetImporting: (value: boolean) => void;
    isDoTransaction: boolean;
    addressId?: string;
    setCart: (cart: Cart) => void;
    getCart: () => Promise<void>;
    calculateCart: () => Promise<void>;
    updateItemQuantity: (
        params: UpdateItemQuantityRequestParams,
        calculate?: boolean,
    ) => Promise<void>;
    addItem: (
        params: AddItemToCartRequestParams | AddItemToCartRequestParams[],
        calculate?: boolean,
    ) => Promise<void>;
    numItemsInCart: () => number;
    deleteItem: (
        params: DeleteItemRequestParams,
        calculate?: boolean,
    ) => Promise<void>;
    deleteAllItems: () => Promise<void>;
    setAddressId: (address: string) => void;
    assignOrderAddressAndCheckout: (
        addressId: string,
        paymentMethod: PaymentMethod,
        taxAddress: string,
        isTax: boolean,
        paymentMethodData?: {
            id: string;
            type: string;
            method: string;
            installment_option?: any;
            handle: string;
        },
        isQuote?: boolean,
        taxId?: string,
    ) => Promise<APIPaymentResult | undefined>;
    addCoupons: (couponCode: string) => Promise<void>;
    addPromoCode: (promoCode: string) => Promise<void>;
    removeCoupon: (index: number) => Promise<void>;
    removePromoCode: () => Promise<void>;
    isHaveOutOfStockSaleItems: () => boolean;
    getQuantityForItem: (handle: string) => number;
    getOtherLineQuantityForItem: (handle: string, lineId: number) => number;
    checkIdCardWithAX: (encIdCard: string) => Promise<AXIDCardResult>;
    varaintLock: Record<string, boolean>;
    handleSetShippingCost: (
        shippingCost: number,
        shippingName?: string,
    ) => void;
    updateShippingCost: (shippingMethod?: ShippingMethod) => Promise<void>;
    handleSetNeedShipping: (needShipping: boolean) => void;
    canSubmit: () => boolean;
    getCartCurrency: (loc: LOCALIZATION) => Promise<any>;
    cartCurrency?: string;
    paymentMethodList?: any;
    currencyCode?: string;
    getShippingMethodCostById?: (
        methodId: string,
    ) => Promise<ShippingMethodCostResult>;
    applyPointDiscount?: (points: number, saleOrderId: string) => Promise<any>;
    enablePointspot?: boolean;
    getPointspotSettings?: () => Promise<any>;
    pointspotAPIKey?: string;
    pointspotBusinessCode?: string;
    psPhoneNumber?: string;
    updatePointspotPhoneNumber?: (phoneNumber: string) => void;
    checkReservePointspot: () => Promise<void>;
    reservedPoint: number;
    cartSetting?: any;
    conversionRate?: number;
    configPoint?: number;
    configValue?: number;
    calculateDiscountFromPoints?: (points: number) => number;
    enabledSubmitButton: boolean;
    setEnabledSubmitButton: (enabled: boolean) => void;
}

const CartContext = createContext<CartContextData>({
    isUpdating: false,
    isImporting: false,
    handleSetImporting: (value: boolean) => {},
    isDoTransaction: false,
    setCart: (cart: Cart) => {},
    getCart: async () => {},
    calculateCart: async () => {},
    updateItemQuantity: async () => {},
    addItem: async () => {},
    numItemsInCart: () => 0,
    deleteItem: async () => {},
    deleteAllItems: async () => {},
    setAddressId: () => {},
    assignOrderAddressAndCheckout: async (
        addressId: string,
        paymentMethod: PaymentMethod,
        taxAddress: string,
        isTax: boolean,
    ) => undefined,
    addCoupons: async (couponCode: string) => {},
    addPromoCode: async (promoCode: string) => {},
    removeCoupon: async (index: number) => {},
    removePromoCode: async () => {},
    isHaveOutOfStockSaleItems: () => false,
    getQuantityForItem: (handle: string) => 0,
    getOtherLineQuantityForItem: (handle: string, lineId: number) => 0,
    checkIdCardWithAX: async (encIdCard: string) => 'CONTINUE',
    varaintLock: {},
    handleSetShippingCost: (shippingCost: number, shippingName?: string) => {},
    updateShippingCost: async (shippingMethod?: ShippingMethod) => {},
    handleSetNeedShipping: (needShipping: boolean) => {},
    canSubmit: () => true,
    getCartCurrency: async (loc: LOCALIZATION) => {},
    cartCurrency: '',
    paymentMethodList: [],
    currencyCode: '',
    getShippingMethodCostById: async (methodId: string) => {
        try {
            const result: ShippingMethodCostResult =
                await getShippingMethodCost(methodId);
            return result;
        } catch (error) {
            console.error('Error fetching shipping method cost:', error);
            throw error;
        }
    },
    applyPointDiscount: async (points: number, saleOrderId: string) => {},
    enablePointspot: false,
    getPointspotSettings: async () => {},
    pointspotAPIKey: '',
    pointspotBusinessCode: '',
    psPhoneNumber: '',
    updatePointspotPhoneNumber: (phoneNumber: string) => {},
    checkReservePointspot: async () => {},
    reservedPoint: 0,
    cartSetting: null,
    conversionRate: 0,
    configPoint: 0,
    configValue: 0,
    calculateDiscountFromPoints: (points: number) => 0,
    enabledSubmitButton: true,
    setEnabledSubmitButton: (enabled: boolean) => {},
});

export interface CartProviderProps {
    children: React.ReactNode;
    loc?: string;
    cartSetting?: any;
}

export const CartProvider = ({
    children,
    loc,
    cartSetting: cartSettingProp,
}: CartProviderProps) => {
    // const { lang } = useTranslation();
    // const lang = "th"
    loc = loc?.toLowerCase();
    const lang = loc ?? 'th';
    const { t } = useTranslation();
    const [cart, setCart] = useState<Cart>();
    const [isUpdating, setUpdating] = useState<boolean>(false);
    const [isImporting, setImporting] = useState<boolean>(false);
    const [addressId, setAddressId] = useState<string>();
    const [isDoTransaction, setTransaction] = useState<boolean>(false);
    const [varaintLock, setVariantLock] = useState<Record<string, boolean>>({});
    const [needShipping, setNeedShipping] = useState<boolean>(false);
    const [cartCurrency, setCartCurrency] = useState<string>('');
    const [currencyCode, setCurrencyCode] = useState<string>('THB');
    const [paymentMethodList, setPaymentMethodList] = useState<any[]>([]);
    const [enablePointspot, setEnablePointspot] = useState<boolean>(false);
    const [pointspotAPIKey, setPointspotAPIKey] = useState<string>('');
    const [pointspotBusinessCode, setPointspotBusinessCode] =
        useState<string>('');
    const [conversionRate, setConversionRate] = useState<number>(0);
    const [psPhoneNumber, setPsPhoneNumber] = useState<string>('');
    const timeOut = 1200; // in seconds (20 minutes)
    const [weight, setWeight] = useState<number>(0); // น้ำหนักของสินค้าในตะกร้า โดยจะเป็นน้ำหนักรวมของสินค้าทั้งหมด
    const [reservedPoint, setReservedPoint] = useState<number>(0); // คะแนนที่ถูกจองไว้กับ Pointspot และ R-Commerce
    const [cartSetting, setCartSetting] = useState<any>(cartSettingProp);
    const [configPoint, setConfigPoint] = useState<number>(0);
    const [configValue, setConfigValue] = useState<number>(0);
    const [enabledSubmitButton, setEnabledSubmitButton] =
        useState<boolean>(true);

    // KBank payment session tracking (similar to Xendit pattern)
    const [kbankPaymentSession, setKbankPaymentSession] = useState<{
        isActive: boolean;
        saleOrderId: string | null;
        paymentType: 'CREDIT_CARD' | 'QR_PAYMENT' | null;
        orderId?: string;
    }>({ isActive: false, saleOrderId: null, paymentType: null });

    const handleSetImporting = (value: boolean) => {
        setImporting(value);
    };

    const handleSetShippingCost = (
        shippingCost: number,
        shippingName?: string,
    ) => {
        const finalPrice =
            (cart?.final_netAmount ?? 0) +
            shippingCost -
            (cart?.final_discount ?? 0);
        setCart((prevCart) =>
            prevCart
                ? {
                      ...prevCart,
                      chargeAmount: shippingCost,
                      final_price: finalPrice,
                      shipmentName: shippingName ?? '',
                  }
                : prevCart,
        );
    };

    const updateShippingCost = async (shippingMethod?: ShippingMethod) => {
        try {
            await addShippingMethod(shippingMethod);
        } catch (error) {
            console.log(error);
        }
    };

    const handleSetNeedShipping = (needShipping: boolean) => {
        setNeedShipping(needShipping);
    };

    const canSubmit = () =>
        (cart?.saleItems ?? []).length > 0 &&
        (!needShipping || !!cart?.shipmentName) &&
        enabledSubmitButton;

    const getCart = async () => {
        try {
            const result = await getUserCart();
            if (result.success) {
                if (result.result.saleItems.length > 0) {
                    const newLock = result.result.saleItems.reduce(
                        (pre, curr) => {
                            if (varaintLock[curr.msProductVariantId]) {
                                return {
                                    ...pre,
                                    [curr.msProductVariantId]:
                                        varaintLock[curr.msProductVariantId],
                                };
                            }

                            return {
                                ...pre,
                                [curr.msProductVariantId]: false,
                            };
                        },
                        {} as Record<string, boolean>,
                    );
                    setVariantLock(newLock);
                } else {
                    setVariantLock({});
                }

                setCart(result.result);
            }
        } catch (error) {
            console.log(error);
        }
    };

    const calculateCart = async () => {
        try {
            const result = await calculateUserCart();
            if (result.success) {
                setCart(result.result);
            }
        } catch (error) {
            console.log(error);
        }
    };

    const updateItemQuantity = async (
        params: UpdateItemQuantityRequestParams,
        calculate?: boolean,
    ) => {
        const currentItem = (cart?.saleItems ?? []).find(
            (si) =>
                si.lineId === params.lineId &&
                si.msProductVariantId === params.idVariant,
        );
        const quantityDelta = params.updateQty - (currentItem?.quantity ?? 0);
        setUpdating(true);
        setVariantLock((prev) => {
            return {
                ...prev,
                [params.idVariant]: true,
            };
        });
        try {
            const result = await updateCartItemQuantity(params);
            if (result.success) {
                if (currentItem && quantityDelta !== 0) {
                    const gtmItem = buildGtmCartItem(
                        currentItem,
                        Math.abs(quantityDelta),
                        lang,
                    );
                    if (quantityDelta > 0) {
                        pushAddToCart([gtmItem], currencyCode);
                    } else {
                        pushRemoveFromCart([gtmItem], currencyCode);
                    }
                }
                if (calculate) {
                    const cartResult = await calculateUserCart();
                    if (cartResult.success) {
                        setCart((currentValues) => ({
                            ...currentValues,
                            ...cartResult.result,
                            chargeAmount: currentValues?.chargeAmount ?? 0,
                            shipmentName: currentValues?.shipmentName ?? '',
                            final_price:
                                cartResult.result.final_price +
                                (currentValues?.chargeAmount ?? 0),
                        }));
                        setUpdating(false);
                        setVariantLock((prev) => {
                            return {
                                ...prev,
                                [params.idVariant]: false,
                            };
                        });
                    } else {
                        setCart((currentValues) => ({
                            ...currentValues,
                            ...result.result,
                            chargeAmount: currentValues?.chargeAmount ?? 0,
                            shipmentName: currentValues?.shipmentName ?? '',
                            final_price:
                                result.result.final_price +
                                (currentValues?.chargeAmount ?? 0),
                        }));
                        setUpdating(false);
                        setVariantLock((prev) => {
                            return {
                                ...prev,
                                [params.idVariant]: false,
                            };
                        });
                    }
                } else {
                    setCart((currentValues) => ({
                        ...currentValues,
                        ...result.result,
                        chargeAmount: currentValues?.chargeAmount ?? 0,
                        shipmentName: currentValues?.shipmentName ?? '',
                        final_price:
                            result.result.final_price +
                            (currentValues?.chargeAmount ?? 0),
                    }));
                    setUpdating(false);
                    setVariantLock((prev) => {
                        return {
                            ...prev,
                            [params.idVariant]: false,
                        };
                    });
                }
            } else {
                setUpdating(false);
                throw new Error(
                    result.error_friendly_message[lang as 'en' | 'th'],
                );
            }
        } catch (error) {
            console.log(error);
            setUpdating(false);
            setVariantLock((prev) => {
                return {
                    ...prev,
                    [params.idVariant]: false,
                };
            });
            throw error;
        }
    };

    const addItem = async (
        params: AddItemToCartRequestParams | AddItemToCartRequestParams[],
        calculate?: boolean,
    ) => {
        try {
            const result = await addItemToCart(params, calculate);
            if (result.success) {
                setCart(result.result);
                const addedParams = Array.isArray(params) ? params : [params];
                const gtmItems = addedParams
                    .map((p) => {
                        const saleItem = (
                            result.result.saleItems ?? []
                        ).find(
                            (si: SaleOrderItem) =>
                                si.msProductVariantId === p.idVariant,
                        );
                        return saleItem
                            ? buildGtmCartItem(saleItem, p.quantity, lang)
                            : null;
                    })
                    .filter((i): i is NonNullable<typeof i> => i !== null);
                pushAddToCart(gtmItems, currencyCode);
            } else {
                throw new Error(
                    result.error_friendly_message[lang as 'en' | 'th'],
                );
            }
        } catch (error) {
            console.log(error);
            throw error;
        }
    };

    const numItemsInCart = () => {
        return (cart?.saleItems ?? []).reduce(
            (prev, current) => (prev += current.quantity),
            0,
        );
    };

    const clearOptionalCartCondition = async (cartId: string) => {
        if (!cartId) return;
        try {
            await setCartConditionValue(cartId, null, null);
        } catch (e) {
            console.log('[clearOptionalCartCondition] error:', e);
        }
    };

    const deleteAllItems = async () => {
        try {
            setUpdating(true);
            setVariantLock({});
            const result = await deleteAllCartItems();
            if (result.success) {
                await clearOptionalCartCondition(result.result.id);
                setCart(result.result);
                setUpdating(false);
            } else {
                setUpdating(false);
                throw new Error(
                    result.error_friendly_message[lang as 'en' | 'th'],
                );
            }
        } catch (error) {
            console.log(error);
            setUpdating(false);
            throw error;
        }
    };

    const deleteItem = async (
        params: DeleteItemRequestParams,
        calculate?: boolean,
    ) => {
        const removedItem = (cart?.saleItems ?? []).find(
            (si) =>
                si.lineId === params.lineId &&
                si.msProductVariantId === params.idVariant,
        );
        try {
            setUpdating(true);
            setVariantLock((prev) => {
                return {
                    ...prev,
                    [params.idVariant]: true,
                };
            });
            const result = await deleteCartItem(params);
            if (result.success) {
                if (removedItem) {
                    pushRemoveFromCart(
                        [
                            buildGtmCartItem(
                                removedItem,
                                removedItem.quantity,
                                lang,
                            ),
                        ],
                        currencyCode,
                    );
                }
                const becameEmpty =
                    (result.result.saleItems ?? []).length === 0;
                if (becameEmpty) {
                    await clearOptionalCartCondition(result.result.id);
                }
                if (calculate) {
                    const cartResult = await calculateUserCart();
                    if (cartResult.success) {
                        setCart((currentValues) => ({
                            ...currentValues,
                            ...cartResult.result,
                            chargeAmount: currentValues?.chargeAmount ?? 0,
                            shipmentName: currentValues?.shipmentName ?? '',
                            final_price:
                                cartResult.result.final_price +
                                (currentValues?.chargeAmount ?? 0),
                        }));
                        setUpdating(false);
                        setVariantLock((prev) => {
                            return {
                                ...prev,
                                [params.idVariant]: false,
                            };
                        });
                    } else {
                        setCart((currentValues) => ({
                            ...currentValues,
                            ...result.result,
                            chargeAmount: currentValues?.chargeAmount ?? 0,
                            shipmentName: currentValues?.shipmentName ?? '',
                            final_price:
                                result.result.final_price +
                                (currentValues?.chargeAmount ?? 0),
                        }));
                        setUpdating(false);
                        setVariantLock((prev) => {
                            return {
                                ...prev,
                                [params.idVariant]: false,
                            };
                        });
                    }
                } else {
                    setCart((currentValues) => ({
                        ...currentValues,
                        ...result.result,
                        chargeAmount: currentValues?.chargeAmount ?? 0,
                        shipmentName: currentValues?.shipmentName ?? '',
                        final_price:
                            result.result.final_price +
                            (currentValues?.chargeAmount ?? 0),
                    }));
                    setUpdating(false);
                    setVariantLock((prev) => {
                        return {
                            ...prev,
                            [params.idVariant]: false,
                        };
                    });
                }
            } else {
                setUpdating(false);
                throw new Error(
                    result.error_friendly_message[lang as 'en' | 'th'],
                );
            }
        } catch (error) {
            console.log(error);
            setUpdating(false);
            setVariantLock((prev) => {
                return {
                    ...prev,
                    [params.idVariant]: false,
                };
            });
            throw error;
        }
    };

    const checkIdCardWithAX = async (
        encIdCard: string,
    ): Promise<AXIDCardResult> => {
        setTransaction(true);
        try {
            const resultIdCard = await updateIdCard(encIdCard);
            console.log(
                `result updateIdCard = ${JSON.stringify(resultIdCard)}`,
            );
            if (!resultIdCard.success) {
                setTransaction(false);
                throw new Error(
                    resultIdCard.error_friendly_message[lang as 'en' | 'th'],
                );
            }
            setTransaction(false);

            if (resultIdCard.result && resultIdCard.result.ax_result) {
                return resultIdCard.result.ax_result;
            }
            throw new Error('ไม่สามารถตรวจสอบเลขบัตรประชาชนได้');
        } catch (error) {
            setTransaction(false);
            console.log(`result updateIdCard error = ${error}`);
            throw error;
        }

        // setTransaction(false);
        // return "FORCE_LOGIN"
    };

    const checkReservePointspot = async () => {
        if (cart && enablePointspot) {
            try {
                const result = await checkReservedPoints();
                if (result.success) {
                    setReservedPoint(result.result);
                }
            } catch (error) {
                setReservedPoint(0);
            }
        } else {
            setReservedPoint(0);
        }
    };

    const assignOrderAddressAndCheckout = async (
        addressId: string,
        paymentMethod: PaymentMethod,
        taxAddress: string,
        isTax: boolean,
        paymentMethodData?: {
            id: string;
            type: string;
            method: string;
            installment_option?: any;
            handle: string;
        },
        isQuote?: boolean,
        taxId?: string,
    ) => {
        if (cart) {
            try {
                setTransaction(true);
                await updateOrderAddress(cart.id, addressId);
                await updateTaxInvoiceAddress(cart.id, taxAddress, isTax, taxId);
                const host = `${window.location.protocol}//${window.location.host}`;

                // Omise
                if (paymentMethodData?.method === 'OMISE') {
                    const result = await omiseInit(
                        paymentMethodData?.id ?? '',
                        cart.id,
                        host,
                    );
                    if (result.success) {
                        getCart();
                        setTransaction(false);
                        return result;
                    } else {
                        setTransaction(false);
                        throw new Error(
                            result.error_friendly_message[lang as 'en' | 'th'],
                        );
                    }
                }

                // Kbank
                if (
                    paymentMethodData?.method === 'KBANK' &&
                    paymentMethodData.type === 'CREDIT_CARD'
                ) {
                    const result = await KbankInit(
                        paymentMethodData?.id ?? '',
                        cart.id,
                        host,
                    );
                    if (result.success) {
                        getCart();
                        setTransaction(false);
                        return result;
                    } else {
                        setTransaction(false);
                        throw new Error(
                            result.error_friendly_message[lang as 'en' | 'th'],
                        );
                    }
                } else if (
                    paymentMethodData?.method === 'KBANK' &&
                    paymentMethodData.type === 'QR_PAYMENT'
                ) {
                    const result = await KbankQrCode(cart.id);
                    if (result.success) {
                        getCart();
                        setTransaction(false);
                        return result;
                    } else {
                        setTransaction(false);
                        throw new Error(
                            result.error_friendly_message[lang as 'en' | 'th'],
                        );
                    }
                }

                // Xendit Credit Card — open modal first; checkout + session creation happen after user submits card
                if (
                    paymentMethod === 'CREDIT_CARD' &&
                    paymentMethodData?.method === 'XENDIT'
                ) {
                    setTransaction(false);
                    return {
                        success: true,
                        statusCode: 200,
                        error: [],
                        error_friendly_message: { en: '', th: '' },
                        result: {
                            open_xendit_modal: true,
                            webPaymentUrl: '',
                            paymentToken: '',
                            respCode: '',
                            respDesc: '',
                        },
                    };
                }

                let checkoutResult;
                if (paymentMethodData?.method == 'TWOC2P') {
                    checkoutResult = await checkout(isQuote, true);
                } else if (paymentMethodData?.method == 'XENDIT') {
                    checkoutResult = await checkout(isQuote, true);
                } else {
                    checkoutResult = await checkout(isQuote, false);
                }

                if (checkoutResult.success) {
                    if (
                        paymentMethod === 'CREDIT_CARD' &&
                        paymentMethodData?.method === 'TWOC2P'
                    ) {
                        const result = await payment2c2p({
                            saleOrdersId: cart.id,
                            frontendReturnUrl: `${host}/api/payment/confirm`,
                            description: 'test payment with 2c2p',
                            paymentMethodId: paymentMethodData?.id ?? '',
                            currencyCode: currencyCode,
                        });
                        getCart();
                        setTransaction(false);
                        return result;
                    } else if (
                        paymentMethod === 'QR_PAYMENT' &&
                        paymentMethodData?.method === 'TWOC2P'
                    ) {
                        const result = await payment2c2p({
                            saleOrdersId: cart.id,
                            frontendReturnUrl: `${host}/api/payment/confirm`,
                            description: 'test payment with 2c2p',
                            paymentMethodId: paymentMethodData?.id ?? '',
                            currencyCode: currencyCode,
                        });
                        getCart();
                        setTransaction(false);
                        return result;
                    } else if (
                        paymentMethod === 'INSTALLMENT' &&
                        paymentMethodData?.method === 'XENDIT'
                    ) {
                        const result = await createXenditInstallmentPayment({
                            country: 'TH',
                            type: 'PAY',
                            paymentMethodType: paymentMethodData?.type ?? '',
                            channel_code: paymentMethodData?.handle ?? '',
                            channel_properties: {
                                installment_configuration: {
                                    terms:
                                        paymentMethodData?.installment_option
                                            ?.terms ?? 0,
                                    interval:
                                        paymentMethodData?.installment_option
                                            ?.interval ?? '',
                                },
                                success_return_url:
                                    process.env.NODE_ENV === 'development'
                                        ? `https://www.google.com`
                                        : `${host}/api/payment/xendit-confirm`,
                                failure_return_url:
                                    process.env.NODE_ENV === 'development'
                                        ? `https://www.google.com`
                                        : `${host}/api/payment/xendit-confirm`,
                            },
                            saleOrderId: cart.id,
                        });
                        if (result.success) {
                            getCart();
                            setTransaction(false);
                            return result;
                        } else {
                            setTransaction(false);
                            throw new Error(
                                result.error_friendly_message[
                                    lang as 'en' | 'th'
                                ],
                            );
                        }
                    } else if (paymentMethodData?.method === 'XENDIT') {
                        const result = await createXenditPaymentLink({
                            session_type: 'PAY',
                            mode: 'PAYMENT_LINK',
                            country: 'TH',
                            locale: lang.toUpperCase(),
                            success_return_url:
                                process.env.NODE_ENV === 'development'
                                    ? `https://www.google.com`
                                    : `${host}/api/payment/xendit-confirm`,
                            cancel_return_url:
                                process.env.NODE_ENV === 'development'
                                    ? `https://www.google.com`
                                    : `${host}/cart`,
                            allowed_payment_channels:
                                list_payment_channels[
                                    paymentMethodData?.type as keyof typeof list_payment_channels
                                ] ?? [],
                            paymentMethodType: paymentMethodData?.type ?? '',
                        });
                        if (result.success) {
                            setTransaction(false);
                            getCart();
                            return result;
                        } else {
                            setTransaction(false);
                            throw new Error(
                                result.error_friendly_message[
                                    lang as 'en' | 'th'
                                ],
                            );
                        }
                    } else if (
                        paymentMethod === 'TRANSFER' ||
                        paymentMethod === 'COD'
                    ) {
                        const result = await paymentCod({
                            saleOrdersId: cart.id,
                            frontendReturnUrl: '',
                            description: '',
                            paymentMethodId: paymentMethodData?.id ?? '',
                            currencyCode: currencyCode,
                        });
                        if (result.success) {
                            getCart();
                            setTransaction(false);
                            return result;
                        } else {
                            setTransaction(false);
                            throw new Error(
                                result.error_friendly_message[
                                    lang as 'en' | 'th'
                                ],
                            );
                        }
                    } else {
                        // NONE
                        const result = await paymentNone({
                            saleOrdersId: cart.id,
                            frontendReturnUrl: '',
                            description: '',
                            paymentMethodId: paymentMethodData?.id ?? '',
                            currencyCode: currencyCode,
                            isQuote: isQuote,
                        });
                        console.log(`paymentcode = ${JSON.stringify(result)}`);
                        if (result.success) {
                            getCart();
                            setTransaction(false);
                            return result;
                        } else {
                            setTransaction(false);
                            throw new Error(
                                result.error_friendly_message[
                                    lang as 'en' | 'th'
                                ],
                            );
                        }
                    }
                } else {
                    setTransaction(false);
                    throw new Error(
                        checkoutResult.error_friendly_message[
                            lang as 'en' | 'th'
                        ],
                    );
                }
            } catch (error) {
                setTransaction(false);
                throw error;
            }
        }
    };

    const omiseInit = async (
        paymentMethodId: string,
        saleOrdersId: string,
        host: string,
    ): Promise<APIPaymentResult> => {
        return new Promise<APIPaymentResult>((resolve, reject) => {
            try {
                // @ts-ignore
                OmiseCard.open({
                    amount: convertToSmallestUnit(
                        cart?.final_price ?? 0,
                        (currencyCode ?? 'THB') as Currency,
                    ),
                    onCreateTokenSuccess: async (token: any) => {
                        try {
                            const checkoutResult = await checkout();
                            if (!checkoutResult.success) {
                                reject(
                                    new Error(
                                        checkoutResult.error_friendly_message[
                                            lang as 'en' | 'th'
                                        ],
                                    ),
                                );
                            }
                            const result = await paymentOmise({
                                saleOrdersId: saleOrdersId,
                                frontendReturnUrl: `${host}/api/payment/omise-confirm?saleOrderId=${saleOrdersId}`,
                                description: 'Omise Payment',
                                paymentMethodId: paymentMethodId,
                                token: token,
                                currencyCode: currencyCode ?? 'THB',
                            });
                            if (result && result.success) {
                                resolve(result);
                            } else {
                                console.log('Omise payment error', result);
                                reject(
                                    new Error(
                                        result?.error_friendly_message[
                                            lang as 'en' | 'th'
                                        ] || 'Omise payment error',
                                    ),
                                );
                            }
                        } catch (error) {
                            console.error(
                                'Omise payment processing error',
                                error,
                            );
                            reject(error);
                        }
                    },
                    onerror: (error: any) => {
                        console.error('Omise payment error', error);
                        reject(error);
                    },
                    onFormClosed: () => {
                        reject(new Error('Modal closed'));
                    },
                });
            } catch (error) {
                console.error('Omise initialization error', error);
                reject(error);
            }
        });
    };

    const KbankInit = async (
        paymentMethodId: string,
        saleOrdersId: string,
        host: string,
    ): Promise<APIPaymentResult> => {
        return new Promise<APIPaymentResult>((resolve, reject) => {
            let checkForToken: NodeJS.Timeout | undefined;
            let timeoutId: NodeJS.Timeout | undefined;
            try {
                if (window.KPayment) {
                    // Set payment session state
                    setKbankPaymentSession({
                        isActive: true,
                        saleOrderId: saleOrdersId,
                        paymentType: 'CREDIT_CARD',
                    });

                    window.KPayment.setPaymentMethods('card');
                    window.KPayment.setAmount(
                        Math.round((cart?.final_price ?? 0) * 100) / 100,
                    );
                    window.KPayment.create();
                    window.KPayment.onClose(() => {
                        if (checkForToken) clearInterval(checkForToken);
                        if (timeoutId) clearTimeout(timeoutId);

                        // Clear payment session state
                        setKbankPaymentSession({
                            isActive: false,
                            saleOrderId: null,
                            paymentType: null,
                        });
                        setTransaction(false);
                        reject(new Error('Modal closed'));
                    });
                    window.KPayment.show();

                    // Set timeout ONCE before polling starts (fix for timeout bug)
                    timeoutId = setTimeout(() => {
                        if (checkForToken) clearInterval(checkForToken);
                        setKbankPaymentSession({
                            isActive: false,
                            saleOrderId: null,
                            paymentType: null,
                        });
                        setTransaction(false);
                        reject(new Error('Kbank payment timeout'));
                    }, timeOut * 1000);

                    checkForToken = setInterval(async () => {
                        const tokenInput = document.querySelector(
                            'input[name="token"]',
                        ) as HTMLInputElement;
                        const saveCardInput = document.querySelector(
                            'input[name="saveCard"]',
                        ) as HTMLInputElement | null;

                        if (tokenInput && tokenInput.value) {
                            clearInterval(checkForToken);
                            if (timeoutId) clearTimeout(timeoutId);

                            const token = tokenInput.value;
                            const saveCard =
                                saveCardInput?.value === 'true' ? true : false;

                            const checkoutResult = await checkout(false, true);
                            if (!checkoutResult.success) {
                                console.error(
                                    '❌ Checkout failed:',
                                    checkoutResult.error_friendly_message,
                                );
                                setKbankPaymentSession({
                                    isActive: false,
                                    saleOrderId: null,
                                    paymentType: null,
                                });
                                reject(
                                    new Error(
                                        checkoutResult.error_friendly_message[
                                            lang as 'en' | 'th'
                                        ],
                                    ),
                                );
                                return;
                            }

                            const result = await paymentKbank({
                                saleOrdersId: saleOrdersId,
                                frontendReturnUrl: `${host}/api/payment/kbank-confirm`,
                                description: 'Kbank Payment',
                                paymentMethodId: paymentMethodId,
                                token: token,
                                currencyCode: currencyCode ?? 'THB',
                                amount:
                                    Math.round((cart?.final_price ?? 0) * 100) /
                                    100,
                                saveCard: saveCard,
                            });

                            // Clear payment session state on completion
                            setKbankPaymentSession({
                                isActive: false,
                                saleOrderId: null,
                                paymentType: null,
                            });

                            // Cleanup token immediately after use
                            tokenInput.remove();

                            if (
                                result &&
                                result.success &&
                                result?.result?.docNo
                            ) {
                                resolve(result);
                            } else if (
                                result &&
                                result.success &&
                                result?.result?.errorCode &&
                                result?.result?.errorMessage
                            ) {
                                const textCode =
                                    'kbank_payment_failed.' +
                                    result?.result?.errorMessage;
                                const errorMes =
                                    result?.result?.errorCode +
                                    ' ' +
                                    t(textCode, {
                                        defaultValue: 'Kbank payment error',
                                    });
                                console.error(
                                    '❌ KBank payment error:',
                                    errorMes,
                                );
                                reject(new Error(errorMes));
                            } else {
                                console.error(
                                    '❌ KBank payment failed:',
                                    result?.error_friendly_message,
                                );
                                reject(
                                    new Error(
                                        result?.error_friendly_message[
                                            lang as 'en' | 'th'
                                        ] || 'Kbank payment error',
                                    ),
                                );
                            }
                        }
                    }, 1000);
                } else {
                    console.error(
                        '❌ KBank SDK (window.KPayment) not available',
                    );
                    if (checkForToken) clearInterval(checkForToken);
                    if (timeoutId) clearTimeout(timeoutId);
                    setKbankPaymentSession({
                        isActive: false,
                        saleOrderId: null,
                        paymentType: null,
                    });
                }
            } catch (error) {
                console.error('❌ Error in KbankInit:', error);
                if (checkForToken) clearInterval(checkForToken);
                if (timeoutId) clearTimeout(timeoutId);
                setKbankPaymentSession({
                    isActive: false,
                    saleOrderId: null,
                    paymentType: null,
                });
                reject(error);
            }
        });
    };

    const KbankQrCode = async (
        saleOrdersId: string,
    ): Promise<APIPaymentResult> => {
        return new Promise<APIPaymentResult>(async (resolve, reject) => {
            let checkForChargeId: NodeJS.Timeout | undefined;
            let timeoutId: NodeJS.Timeout | undefined;
            try {
                const checkoutResult = await checkout(false, true);
                if (!checkoutResult.success) {
                    console.error(
                        '❌ Checkout failed:',
                        checkoutResult.error_friendly_message,
                    );
                    reject(
                        new Error(
                            checkoutResult.error_friendly_message[
                                lang as 'en' | 'th'
                            ],
                        ),
                    );
                    return;
                }

                const result = await createKbankQROrder({
                    saleOrderId: saleOrdersId,
                });

                // set Order Id
                if (window.KPayment && result && result.success) {
                    // Set payment session state
                    setKbankPaymentSession({
                        isActive: true,
                        saleOrderId: saleOrdersId,
                        paymentType: 'QR_PAYMENT',
                        orderId: result.result.orderId,
                    });
                    window.KPayment.setAmount(
                        Math.round((cart?.final_price ?? 0) * 100) / 100,
                    );
                    window.KPayment.setOrderId(result.result.orderId);
                    window.KPayment.create();
                    window.KPayment.onClose(() => {
                        if (checkForChargeId) clearInterval(checkForChargeId);
                        reject(new Error('QR Kbank Modal closed'));
                    });
                    window.KPayment.show();
                    // Set timeout ONCE before polling starts (fix for timeout bug)
                    timeoutId = setTimeout(() => {
                        if (checkForChargeId) clearInterval(checkForChargeId);
                        setKbankPaymentSession({
                            isActive: false,
                            saleOrderId: null,
                            paymentType: null,
                        });
                        setTransaction(false);
                        reject(new Error('Kbank QR payment timeout'));
                    }, timeOut * 1000);

                    checkForChargeId = setInterval(async () => {
                        // timeout
                        setTimeout(() => {
                            checkForChargeId && clearInterval(checkForChargeId);
                            setTransaction(false);
                            reject(new Error('QR Kbank payment timeout'));
                        }, timeOut * 1000);

                        const chargeIdInput = document.querySelector(
                            'input[name="chargeId"]',
                        ) as HTMLInputElement | null;
                        if (chargeIdInput && chargeIdInput.value) {
                            try {
                                const chargeId = chargeIdInput.value;
                                clearInterval(checkForChargeId);
                                if (timeoutId) clearTimeout(timeoutId);

                                const inquiryResult = await inquireKbankQR(
                                    saleOrdersId,
                                    chargeId,
                                );

                                // Clear payment session state on completion
                                setKbankPaymentSession({
                                    isActive: false,
                                    saleOrderId: null,
                                    paymentType: null,
                                });

                                if (
                                    inquiryResult &&
                                    inquiryResult.success === true
                                ) {
                                    resolve({
                                        success: true,
                                        statusCode: 200,
                                        error: [],
                                        error_friendly_message: {
                                            en: '',
                                            th: '',
                                        },
                                        result: {
                                            webPaymentUrl: '',
                                            paymentToken: '',
                                            respCode: '',
                                            respDesc: '',
                                        },
                                    });
                                } else {
                                    console.error(
                                        '❌ KBank QR payment inquiry failed:',
                                        inquiryResult?.error_friendly_message,
                                    );
                                    reject(
                                        new Error(
                                            inquiryResult
                                                ?.error_friendly_message[
                                                lang as 'en' | 'th'
                                            ] || 'Kbank QR payment error',
                                        ),
                                    );
                                }
                            } catch (e) {
                                console.error(
                                    '❌ Error during KBank QR inquiry:',
                                    e,
                                );
                                if (checkForChargeId)
                                    clearInterval(checkForChargeId);
                                if (timeoutId) clearTimeout(timeoutId);
                                setKbankPaymentSession({
                                    isActive: false,
                                    saleOrderId: null,
                                    paymentType: null,
                                });
                                setTransaction(false);
                                reject(e);
                            }
                        }
                    }, 800);
                } else {
                    console.error(
                        '❌ Failed to create KBank QR order or KPayment SDK not available',
                    );
                    if (checkForChargeId) clearInterval(checkForChargeId);
                    if (timeoutId) clearTimeout(timeoutId);
                    setKbankPaymentSession({
                        isActive: false,
                        saleOrderId: null,
                        paymentType: null,
                    });
                    reject(
                        new Error(
                            result?.error_friendly_message[
                                lang as 'en' | 'th'
                            ] || 'Kbank QR payment error',
                        ),
                    );
                }
            } catch (error) {
                console.error('❌ Error in KbankQrCode:', error);
                if (checkForChargeId) clearInterval(checkForChargeId);
                if (timeoutId) clearTimeout(timeoutId);
                setKbankPaymentSession({
                    isActive: false,
                    saleOrderId: null,
                    paymentType: null,
                });
                reject(error);
            }
        });
    };

    const addCoupons = async (couponCode: string) => {
        setUpdating(true);
        try {
            const result = await setOrderCoupon(
                [...(cart?.discountRemarks ?? []), couponCode],
                cart?.promoCode ?? '',
            );
            if (result.success) {
                setCart(result.result);
                setUpdating(false);

                if (result.result.errorCode) {
                    throw new Error(
                        result.result.errorCode.message[lang as 'en' | 'th'],
                    );
                }
            } else {
                setUpdating(false);
                throw new Error(
                    result.error_friendly_message[lang as 'en' | 'th'],
                );
            }
        } catch (error) {
            console.log(error);
            setUpdating(false);
            throw error;
        }
    };

    const addPromoCode = async (promoCode: string) => {
        setUpdating(true);
        try {
            const result = await setOrderCoupon(
                [...(cart?.discountRemarks ?? [])],
                promoCode,
            );
            if (result.success) {
                setCart(result.result);
                setUpdating(false);

                if (result.result.errorCode) {
                    throw new Error(
                        result.result.errorCode.message[lang as 'en' | 'th'],
                    );
                }
            } else {
                setUpdating(false);
                throw new Error(
                    result.error_friendly_message[lang as 'en' | 'th'],
                );
            }
        } catch (error) {
            console.log(error);
            setUpdating(false);
            throw error;
        }
    };

    const removeCoupon = async (index: number) => {
        setUpdating(true);
        try {
            if ((cart?.discountRemarks ?? []).length <= index) {
                throw new Error('remove_discount_exceed');
            }

            cart?.discountRemarks.splice(index, 1);
            const result = await setOrderCoupon(
                [...(cart?.discountRemarks ?? [])],
                cart?.promoCode ?? '',
            );
            if (result.success) {
                setCart(result.result);
                setUpdating(false);
            } else {
                setUpdating(false);
                throw new Error(
                    result.error_friendly_message[lang as 'en' | 'th'],
                );
            }
        } catch (error) {
            console.log(error);
            setUpdating(false);
            throw error;
        }
    };

    const removePromoCode = async () => {
        setUpdating(true);
        try {
            const result = await setOrderCoupon(
                [...(cart?.discountRemarks ?? [])],
                '',
            );
            if (result.success) {
                setCart(result.result);
                setUpdating(false);
            } else {
                setUpdating(false);
                throw new Error(
                    result.error_friendly_message[lang as 'en' | 'th'],
                );
            }
        } catch (error) {
            console.log(error);
            setUpdating(false);
            throw error;
        }
    };

    const isHaveOutOfStockSaleItems = () => {
        let isHaveOutOfStock = false;
        for (let item of cart?.saleItems ?? []) {
            if (
                item.quantity >
                item.msProductVariant.inventory -
                    item.msProductVariant.inventory_used
            ) {
                isHaveOutOfStock = true;
                break;
            }
        }
        return isHaveOutOfStock;
    };

    const getCartCurrency = async (loc: LOCALIZATION) => {
        const result = await getCurrency(loc);
        setCartCurrency(String(result?.result?.currency ?? ''));
        setCurrencyCode(String(result?.result?.currencyCode ?? 'THB'));
        return result.result;
    };

    const getQuantityForItem = (id: string) => {
        return (
            cart?.saleItems
                .filter((item) => item.msProductVariantId === id)
                .reduce((prev, current) => (prev += current.quantity), 0) ?? 0
        );
    };

    const getOtherLineQuantityForItem = (handle: string, lineId: number) => {
        return (
            cart?.saleItems
                .filter(
                    (item) =>
                        item.msProductHandle === handle &&
                        item.lineId !== lineId,
                )
                .reduce((prev, current) => (prev += current.quantity), 0) ?? 0
        );
    };

    const getShippingMethodCostById = async (methodId: string) => {
        setUpdating(true);
        try {
            const result: ShippingMethodCostResult =
                await getShippingMethodCost(methodId);
            setUpdating(false);
            return result;
        } catch (error) {
            console.error('Error fetching shipping method cost:', error);
            setUpdating(false);
            throw error;
        }
    };

    const applyPointDiscount = async (points: number, saleOrderId: string) => {
        setUpdating(true);
        try {
            const result = await applyPointDiscountAction(points, saleOrderId);
            if (result.success) {
                setCart(result.result);
            } else {
                throw new Error(
                    result.error_friendly_message[lang as 'en' | 'th'],
                );
            }
        } catch (error) {
            console.log('Error applying point discount:', error);
            throw error;
        } finally {
            setUpdating(false);
        }
    };

    const calculateDiscountFromPoints = (points: number) => {
        return points * conversionRate;
    };

    const fetchPointspotSettings = async () => {
        try {
            const settings = await getPointspotSettings();
            if (settings && settings.enabled) {
                setEnablePointspot(true);
                setPointspotAPIKey(settings.apiKey || '');
                setPointspotBusinessCode(settings.businessCode || '');
                setConversionRate(
                    settings.conversionRatePointsToAmountAmountValue /
                        settings.conversionRatePointsToAmountPointValue,
                );
                setConfigPoint(settings.conversionRatePointsToAmountPointValue);
                setConfigValue(
                    settings.conversionRatePointsToAmountAmountValue,
                );
            } else {
                setEnablePointspot(false);
                setPointspotAPIKey('');
                setPointspotBusinessCode('');
                setConversionRate(0);
                setConfigPoint(0);
                setConfigValue(0);
            }
        } catch (error) {
            console.log('Error fetching Pointspot settings:', error);
            setEnablePointspot(false);
            setPointspotAPIKey('');
            setPointspotBusinessCode('');
        }
    };

    const updatePointspotPhoneNumber = async (phoneNumber: string) => {
        if (cart?.pointspotPhoneNumber !== phoneNumber) {
            const result = await setPointspotPhoneNumber(phoneNumber);
            if (result.success) {
                setPsPhoneNumber(phoneNumber);
                setCart(result.result);
            }
        }
    };

    return (
        <CartContext.Provider
            value={{
                cart,
                isImporting,
                handleSetImporting,
                setCart,
                getCart,
                calculateCart,
                updateItemQuantity,
                addItem,
                numItemsInCart,
                deleteItem,
                deleteAllItems,
                isUpdating,
                addressId,
                setAddressId,
                assignOrderAddressAndCheckout,
                isDoTransaction,
                addCoupons,
                addPromoCode,
                removeCoupon,
                removePromoCode,
                isHaveOutOfStockSaleItems,
                getQuantityForItem,
                getOtherLineQuantityForItem,
                checkIdCardWithAX,
                varaintLock,
                handleSetShippingCost,
                updateShippingCost,
                handleSetNeedShipping,
                canSubmit,
                getCartCurrency,
                cartCurrency,
                paymentMethodList,
                currencyCode,
                getShippingMethodCostById,
                applyPointDiscount,
                enablePointspot,
                getPointspotSettings: fetchPointspotSettings,
                pointspotAPIKey,
                pointspotBusinessCode,
                psPhoneNumber,
                updatePointspotPhoneNumber: updatePointspotPhoneNumber,
                checkReservePointspot: checkReservePointspot,
                reservedPoint: reservedPoint,
                cartSetting,
                conversionRate: conversionRate,
                configPoint: configPoint,
                configValue: configValue,
                calculateDiscountFromPoints: calculateDiscountFromPoints,
                enabledSubmitButton: enabledSubmitButton,
                setEnabledSubmitButton: setEnabledSubmitButton,
            }}
        >
            {children}
        </CartContext.Provider>
    );
};

export const useCart = (p0: { loc: string }) => {
    return useContext(CartContext);
};
